import {
  ActivityFilters,
  activityFiltersFromParams,
  activityFiltersToParams,
} from "./activity-filters";

/**
 * Activity search (MOSA-933).
 *
 * `q` lives in the page URL. `tz` does not. The list and the CSV send `tz`
 * as the viewer's IANA zone on the API request only.
 *
 * The reading line is the API `interpreted` object. This file does not parse
 * the search text.
 */

export type ActivitySearchInterpreted = {
  actorKey: string | null;
  actorName: string | null;
  tab: string | null;
  from: string | null;
  to: string | null;
  timeZone: string | null;
  text: string[];
  ambiguous: string[];
};

const MONTHS = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
] as const;

/** IANA zone of this browser. UTC when the platform does not name one. */
export const viewerTimeZone = (): string => {
  try {
    const zone = Intl.DateTimeFormat().resolvedOptions().timeZone?.trim();
    return zone || "UTC";
  } catch {
    return "UTC";
  }
};

/** 1-based page from the Activity URL. Missing or invalid is page 1. */
export const activityPageFromParams = (params: URLSearchParams): number => {
  const raw = params.get("page");
  if (!raw) return 1;
  const page = Number(raw);
  if (!Number.isInteger(page) || page < 1) return 1;
  return page;
};

/**
 * Apply or clear the search text in the page URL.
 *
 * Keeps person, tab, date and source. Sets page to 1. Drops `tz` when the
 * current URL has one.
 */
export const applyActivitySearch = (
  params: URLSearchParams,
  q: string
): URLSearchParams => {
  const filters: ActivityFilters = activityFiltersFromParams(params);
  const trimmed = q.trim();
  if (trimmed) filters.q = trimmed;
  else delete filters.q;
  const next = activityFiltersToParams(filters);
  next.set("page", "1");
  next.delete("tz");
  return next;
};

type CivilDay = { y: number; m: number; d: number };

const zoneForReading = (timeZone: string | null | undefined): string => {
  const zone = timeZone?.trim() || "UTC";
  return zone.toUpperCase() === "UTC" ? "UTC" : zone;
};

/** Calendar day of an instant in an IANA zone. Null when the zone is invalid. */
const civilDay = (iso: string, timeZone: string): CivilDay | null => {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return null;
  try {
    const parts = new Intl.DateTimeFormat("en-GB", {
      timeZone,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    }).formatToParts(date);
    const num = (type: Intl.DateTimeFormatPartTypes): number => {
      const value = parts.find((part) => part.type === type)?.value;
      return value ? Number(value) : NaN;
    };
    const y = num("year");
    const m = num("month");
    const d = num("day");
    if (!y || !m || !d) return null;
    return { y, m, d };
  } catch {
    return null;
  }
};

/** The calendar day before `day`. Uses the date, not a 24 hour step. */
const previousCivilDay = (day: CivilDay): CivilDay => {
  const date = new Date(Date.UTC(day.y, day.m - 1, day.d));
  date.setUTCDate(date.getUTCDate() - 1);
  return {
    y: date.getUTCFullYear(),
    m: date.getUTCMonth() + 1,
    d: date.getUTCDate(),
  };
};

const formatCivilDay = (day: CivilDay): string =>
  `${day.d} ${MONTHS[day.m - 1]} ${day.y}`;

const terms = (values: unknown): string[] =>
  Array.isArray(values)
    ? values.filter(
        (term): term is string => typeof term === "string" && term.trim() !== ""
      )
    : [];

/**
 * One reading line from `interpreted`.
 *
 * Skips empty parts. Separates the rest with ". ".
 * `to` is exclusive, so the last civil day is the day before `to`.
 */
export const activitySearchReading = (
  interpreted: ActivitySearchInterpreted | null | undefined
): string => {
  if (!interpreted) return "";
  const parts: string[] = [];
  const actorName = interpreted.actorName?.trim();
  if (actorName) parts.push(`Person: ${actorName}`);
  const tab = interpreted.tab?.trim();
  if (tab) parts.push(`Tab: ${tab}`);

  const from = interpreted.from?.trim();
  const to = interpreted.to?.trim();
  if (from && to) {
    const zone = zoneForReading(interpreted.timeZone);
    const start = civilDay(from, zone);
    const endExclusive = civilDay(to, zone);
    if (start && endExclusive) {
      const end = previousCivilDay(endExclusive);
      parts.push(
        `Dates: ${formatCivilDay(start)} to ${formatCivilDay(end)}`
      );
    }
  }

  const text = terms(interpreted.text);
  if (text.length) parts.push(`Text: ${text.join(", ")}`);
  const ambiguous = terms(interpreted.ambiguous);
  if (ambiguous.length) {
    parts.push(`Also used as text: ${ambiguous.join(", ")}`);
  }
  return parts.join(". ");
};

/** Tolerant parse of the list `interpreted` object. Null when it is absent. */
export const asActivitySearchInterpreted = (
  body: unknown
): ActivitySearchInterpreted | null => {
  if (!body || typeof body !== "object") return null;
  const raw = body as Partial<ActivitySearchInterpreted>;
  return {
    actorKey: typeof raw.actorKey === "string" ? raw.actorKey : null,
    actorName: typeof raw.actorName === "string" ? raw.actorName : null,
    tab: typeof raw.tab === "string" ? raw.tab : null,
    from: typeof raw.from === "string" ? raw.from : null,
    to: typeof raw.to === "string" ? raw.to : null,
    timeZone: typeof raw.timeZone === "string" ? raw.timeZone : null,
    text: terms(raw.text),
    ambiguous: terms(raw.ambiguous),
  };
};
